import { useEffect, useMemo, useRef, useState } from 'react';
import { MaterialReactTable, MRT_Row, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Add, Edit, Visibility } from '@mui/icons-material';
import { Autocomplete, Button, darken, debounce, Divider, Grid, IconButton, lighten, styled, TextField, Typography } from '@mui/material';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { IClienteTable, ITipoUsuario, IVendedor } from '../../../../../application/models';
import { formatCnpj } from '../../../../../utils';
import { buildClienteTextSearchFiltersFromTerm } from '../../../../../utils/cliente-busca-unificada';
import { SkeletonVelesTable } from '../../../../components/skeleton-veles';
import { useUIService } from '../../../../../use-cases/ui';
import { useClientesService } from '../../../../../use-cases/clientes';
import { useVendedorService } from '../../../../../use-cases/vendedor';
import { format } from 'date-fns';

export const ListaClientes = () => {
  // State
  const { clientes, clienteListFiltros } = useAppSelector((state) => state.clientes)
  const { vendedores } = useAppSelector((state) => state.vendedor)
  const total = useAppSelector((state) => state.clientes.clientes[0]?.TotalLinhas || 0)
  const user = useAppSelector((state) => state.user.currentUser)

  // Use State
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })
  // Useful variables
  const isVelesUser = user?.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE
  const isVendedor = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL
  const isSupervisorComercial = user?.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL
  const isDemais = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = user?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isCX = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE
  const isCliente = !isVelesUser && !isVendedor && !isSupervisorComercial;

  const podeFiltrarPorVendedor =
    !isVendedor && !isCliente && (isDemais || isTecnologiaDaInformacao || isSupervisorComercial || isCX)

  const canEdit = isDemais || isTecnologiaDaInformacao
  const canCreate = isDemais || isTecnologiaDaInformacao
  // Service
  const { setActivePage } = useUIService()
  const {
    setCreateClientesOpenForm,
    setEditClienteOpenForm,
    setClientesPagination,
    setClientes,
    setClientesPaginaInicial,
    setClientesClearFilters
  } = useClientesService()

  const { setVendedores } = useVendedorService()

  const isFirstRender = useRef(true)
  const lastUserIdRef = useRef<number | null>(null)
  const prevPaginationRef = useRef({ pageIndex: 0, pageSize: 10 })
  const isInitialized = useRef(false)
  const vendedoresInitialized = useRef(false)
  const isClientesRequestInProgress = useRef(false)

  useEffect(() => {
    setClientesPagination({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    })
  }, [pagination.pageIndex, pagination.pageSize, setClientesPagination])

  useEffect(() => {
    if (!user) return;

    // Resetar se o usuário mudou
    if (lastUserIdRef.current !== null && lastUserIdRef.current !== user.IdUsuario) {
      vendedoresInitialized.current = false
    }

    // Verificar se os vendedores já estão carregados no Redux
    // vendedores é um array IVendedor[] diretamente do estado
    const vendedoresJaCarregados = vendedores && Array.isArray(vendedores) && vendedores.length > 0;

    // Se já foram inicializados OU já estão carregados no Redux, não precisa carregar novamente
    if (vendedoresInitialized.current || vendedoresJaCarregados) {
      if (vendedoresJaCarregados && !vendedoresInitialized.current) {
        // Marcar como inicializado se os dados já estão no Redux
        vendedoresInitialized.current = true;
      }
      return;
    }

    if (user.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE &&
      user.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_VELES_COMERCIAL) {
      // Tratar erro silenciosamente para não quebrar a aplicação
      setVendedores().catch(() => {
        // Ignorar erros silenciosamente
      })
      vendedoresInitialized.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, vendedores])

  useEffect(() => {
    if (!user || isClientesRequestInProgress.current) return;

    // Verificar se houve um recarregamento recente via sessionStorage
    // Isso evita requisições duplicadas quando volta da tela de edição
    const lastReloadTimestamp = sessionStorage.getItem('lastClientesReload');
    if (lastReloadTimestamp) {
      const timeSinceLastReload = Date.now() - parseInt(lastReloadTimestamp, 10);
      if (timeSinceLastReload < 2000) {
        // Se houve recarregamento recente (menos de 2 segundos), não executa o useEffect
        // Limpar o timestamp para não bloquear futuras inicializações
        sessionStorage.removeItem('lastClientesReload');
        return;
      }
      // Limpar timestamp antigo
      sessionStorage.removeItem('lastClientesReload');
    }

    if (lastUserIdRef.current === user.IdUsuario && isInitialized.current && clientes && clientes.length > 0) {
      return;
    }

    lastUserIdRef.current = user.IdUsuario;
    isInitialized.current = true;
    isClientesRequestInProgress.current = true;

    const makeRequest = async () => {
      try {
        if (user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
          await setClientes({
            SearchCNPJ: '',
            SearchRazaoSocial: '',
            SearchNomeFantasia: '',
            SearchLoginCliente: '',
            SearchInclusaoMaxima: undefined,
            SearchInclusaoMinima: undefined,
            SearchIdUsuario: user.IdUsuario,
            searchIdVendedor: user.IdUsuario,
            SearchIdCliente: 0
          });
        } else if (user.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL ||
          user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS ||
          user.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO ||
          user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE) {
          await setClientes({
            SearchCNPJ: '',
            SearchRazaoSocial: '',
            SearchNomeFantasia: '',
            SearchLoginCliente: '',
            SearchInclusaoMaxima: undefined,
            SearchInclusaoMinima: undefined,
            SearchIdUsuario: user.IdUsuario,
            searchIdVendedor: 0,
            SearchIdCliente: 0
          });
        } else if (user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
          await setClientes({
            SearchCNPJ: '',
            SearchRazaoSocial: '',
            SearchNomeFantasia: '',
            SearchLoginCliente: '',
            SearchInclusaoMaxima: undefined,
            SearchInclusaoMinima: undefined,
            SearchIdUsuario: user.IdUsuario,
            searchIdVendedor: 0,
            SearchIdCliente: user.IdCliente || 0
          });
        }
      } finally {
        isClientesRequestInProgress.current = false;
      }
    };

    makeRequest();
  }, [setClientes, user]);

  const debounceSearchTerm = debounce((data: {
    searchTerm?: string
    idVendedor?: number
    somenteVendedor?: boolean
  }) => {
    if (!user) return

    const somenteVendedor = Boolean(data.somenteVendedor)
    const textFilters = somenteVendedor
      ? buildClienteTextSearchFiltersFromTerm('')
      : buildClienteTextSearchFiltersFromTerm(data.searchTerm ?? '')

    if (user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
      setClientes({
        ...textFilters,
        SearchInclusaoMaxima: undefined,
        SearchInclusaoMinima: undefined,
        SearchIdUsuario: user.IdUsuario,
        searchIdVendedor: user.IdUsuario,
        SearchIdCliente: 0
      })
    } else if (
      user.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL ||
      user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE ||
      user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS ||
      user.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
    ) {
      const vendedorId = somenteVendedor ? (data.idVendedor ?? 0) : 0
      setClientes({
        ...textFilters,
        SearchInclusaoMaxima: undefined,
        SearchInclusaoMinima: undefined,
        SearchIdUsuario: user.IdUsuario,
        searchIdVendedor: vendedorId,
        SearchIdCliente: 0
      })
    } else if (user.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
      setClientes({
        ...textFilters,
        SearchInclusaoMaxima: undefined,
        SearchInclusaoMinima: undefined,
        SearchIdUsuario: user.IdUsuario,
        searchIdVendedor: 0,
        SearchIdCliente: user.IdCliente || 0
      })
    }
  }, 700)

  const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === 'light'
        ? lighten(theme.palette.primary.light, 0.85)
        : darken(theme.palette.primary.main, 0.8),
  }));

  const GroupItems = styled('ul')({
    padding: 0,
  });

  const columns = useMemo<MRT_ColumnDef<IClienteTable>[]>(() => {
    if (!user) return [];

    const isDemaisInterno = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS || user?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO;
    const canEditInterno = isDemaisInterno;

    const columnsToSet: MRT_ColumnDef<IClienteTable>[] = [
      {
        header: `${canEditInterno ? 'Editar' : 'Visualizar'}`,
        size: 50,
        enableColumnActions: false,
        Cell: ({ row }: { row: MRT_Row<IClienteTable> }) => (
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              setEditClienteOpenForm({
                open: true,
                cliente: row.original
              });
            }}
          >
            {
              canEditInterno ? <Edit />
                : <Visibility />
            }
          </IconButton>
        ),
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        },
      },
      {
        accessorKey: 'LoginCliente',
        header: 'Login',
        enableColumnFilter: false,
        size: 150,
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        }
      },
      {
        accessorKey: 'CNPJ',
        header: 'CNPJ',
        size: 150,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        },
        Cell: ({ cell }) => <span>{
          formatCnpj(cell.getValue<string>())
        }</span>
      },
      {
        accessorKey: 'RazaoSocial',
        header: 'Razão Social',
        size: 150,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        }
      },
      {
        accessorKey: 'NomeFantasia',
        header: 'Nome Fantasia',
        enableColumnFilter: false,
        size: 150,
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        }
      },
      {
        accessorKey: 'LoginUsuarioVendedor',
        header: 'Vendedor',
        enableColumnFilter: false,
        size: 250,
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        },
        minSize: 250
      },
      {
        accessorKey: 'Inclusao',
        header: 'Data de Inclusão',
        size: 150,
        enableColumnFilter: false,
        accessorFn: (rowData: any) => {
          return new Date(rowData.Inclusao);
        },
        Cell: ({ cell }) => <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>,
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Atualizacao',
        header: 'Data de Atualização',
        enableColumnFilter: false,
        size: 150,
        accessorFn: (rowData: any) => {
          return rowData.Atualizacao ? new Date(rowData.Atualizacao) : null
        },
        Cell: ({ cell }) => {
          if (cell.row.original.Atualizacao) {
            return <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>
          }
        },
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'Ativo',
        header: 'Status',
        size: 100,
        enableColumnFilter: false,
        accessorFn: (rowData: any) => {
          return rowData.Ativo ? 'Ativo' : 'Inativo';
        },
        Cell: ({ cell }) => {
          const isActive = cell.getValue<string>() === 'Ativo'

          return (
            <Typography
              color="Background"
              variant='caption'
              style={{
                display: 'inline-block',
                padding: '4px 8px',
                borderRadius: '8px',
                fontWeight: 'bold',
                backgroundColor: isActive ? 'green' : 'red',
                textAlign: 'center'
              }}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </Typography>
          )
        },
        muiTableHeadCellProps: {
          align: 'center' as 'center',
        },
        muiTableBodyCellProps: {
          align: 'center' as 'center',
        },
      },
    ]

    return columnsToSet.filter((column) => {
      if (canEditInterno) {
        return column
      } else {
        if (column.accessorKey !== 'LoginUsuarioVendedor') {
          return column
        } else {
          return false
        }
      }
    })
  },
    [user, setEditClienteOpenForm],
  );

  const table = useMaterialReactTable({
    columns,
    data: clientes,
    columnFilterDisplayMode: 'popover',
    enableColumnOrdering: false,
    manualFiltering: false,
    enableSorting: true,
    enableColumnActions: false,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enableExpandAll: false,
    enableDensityToggle: false,
    localization: MRT_Localization_PT_BR,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading: loading
    },
    rowCount: total,
    manualPagination: true
  })

  useEffect(() => {
    setLoading(false)
  }, [])
  useEffect(() => {
    // SÓ EXECUTAR se não for primeira renderização E se já tiver filtros aplicados

    if (!isFirstRender.current &&
      (clienteListFiltros.SearchCNPJ ||
        clienteListFiltros.SearchRazaoSocial ||
        clienteListFiltros.SearchNomeFantasia ||
        clienteListFiltros.searchLoginCliente ||
        clienteListFiltros.searchIdVendedor)) {

      setClientesClearFilters({
        SearchCNPJ: clienteListFiltros.SearchCNPJ || '',
        SearchRazaoSocial: clienteListFiltros.SearchRazaoSocial || '',
        SearchNomeFantasia: clienteListFiltros.SearchNomeFantasia || '',
        SearchLoginCliente: clienteListFiltros.searchLoginCliente || '',
        SearchInclusaoMaxima: clienteListFiltros.searchInclusaoMaxima || new Date(),
        SearchInclusaoMinima: clienteListFiltros.searchInclusaoMinima || new Date(),
        SearchIdUsuario: Number(user?.IdUsuario) || 0,
        SearchIdVendedor: clienteListFiltros.searchIdVendedor || 0,
        SearchIdCliente: Number(user?.IdCliente)
      })
      setLoading(false);
      isFirstRender.current = true;
    }
  }, [clienteListFiltros,
    clienteListFiltros.searchIdVendedor, clienteListFiltros.SearchCNPJ,
    clienteListFiltros.searchInclusaoMaxima, clienteListFiltros.searchInclusaoMinima,
    clienteListFiltros.searchLoginCliente, clienteListFiltros.SearchNomeFantasia,
    clienteListFiltros.SearchRazaoSocial, setClientes, setClientesClearFilters,
    setClientesPaginaInicial, setLoading, user?.IdCliente, user?.IdUsuario])
  useEffect(() => {
    setClientesPagination({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize
    })
  }, [pagination.pageIndex, pagination.pageSize, setClientesPagination])

  useEffect(() => {
    if (!user || isClientesRequestInProgress.current) return;

    const paginationChanged =
      prevPaginationRef.current.pageIndex !== pagination.pageIndex ||
      prevPaginationRef.current.pageSize !== pagination.pageSize;

    if (paginationChanged) {
      prevPaginationRef.current = { ...pagination };
      isClientesRequestInProgress.current = true;

      const makeRequest = async () => {
        try {
          await setClientes({
            SearchCNPJ: clienteListFiltros.SearchCNPJ || '',
            SearchRazaoSocial: clienteListFiltros.SearchRazaoSocial || '',
            SearchNomeFantasia: clienteListFiltros.SearchNomeFantasia || '',
            SearchLoginCliente: clienteListFiltros.searchLoginCliente || '',
            SearchInclusaoMaxima: clienteListFiltros.searchInclusaoMaxima || new Date(),
            SearchInclusaoMinima: clienteListFiltros.searchInclusaoMinima || new Date(),
            SearchIdUsuario: user.IdUsuario,
            searchIdVendedor: clienteListFiltros.searchIdVendedor || 0,
            SearchIdCliente: user.IdCliente,
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize
          });
        } finally {
          isClientesRequestInProgress.current = false;
        }
      };

      makeRequest();
    }
  }, [pagination, user, clienteListFiltros, setClientes]);

  useEffect(() => {
    return () => {
      isClientesRequestInProgress.current = false;

      setClientesPagination({ page: 1, limit: 10 });
      setClientesClearFilters({
        SearchCNPJ: '',
        SearchRazaoSocial: '',
        SearchNomeFantasia: '',
        SearchLoginCliente: '',
        SearchInclusaoMaxima: new Date(),
        SearchInclusaoMinima: new Date(),
        SearchIdUsuario: 0,
        SearchIdVendedor: 0,
        SearchIdCliente: 0
      });
    };
  }, [setClientesClearFilters, setClientesPagination]);

  return (
    loading ? (
      <SkeletonVelesTable />
    ) : <>
      <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
        {
          canEdit ? 'Lista de Clientes'
            : 'Dados e produtos vinculados à sua empresa'
        }
      </Typography>
      <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />
      <Grid container justifyContent={canCreate ? "space-between" : "flex-end"} alignItems="center" my={1.5}>
        {
          canCreate && (
            <Grid item>
              <Button
                style={{
                  marginBottom: '10px',
                }}
                variant="contained"
                color="primary"
                onClick={() => {
                  setCreateClientesOpenForm(true);
                }}
              >
                <Add
                  style={{
                    marginRight: '8px',
                  }}
                />
                Novo Cliente
              </Button>
            </Grid>
          )
        }
        {
          !isCliente && <>
            {podeFiltrarPorVendedor ? (
              <>
                <Grid item xs={4} sx={{ ml: canCreate ? 0 : 'auto' }}>
                  <TextField
                    onChange={(e) =>
                      debounceSearchTerm({ searchTerm: e.target.value, somenteVendedor: false })
                    }
                    fullWidth
                    label="Buscar cliente"
                    placeholder="Login, razão social, nome fantasia ou CNPJ"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Autocomplete
                    fullWidth
                    autoComplete
                    includeInputInList
                    options={[...vendedores]
                      .filter((v) => {
                        if ((isCX || isSupervisorComercial) && v.LoginUsuario.toLowerCase().includes('veles')) {
                          return false;
                        }
                        return true;
                      })
                      .sort((a, b) => a.LoginUsuario.localeCompare(b.LoginUsuario))}
                    groupBy={(option) => option.LoginUsuario[0]}
                    getOptionLabel={(option) => option.LoginUsuario}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <GroupHeader>{params.group}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                      </li>
                    )}
                    onChange={(event: any, newValue: IVendedor | null) => {
                      debounceSearchTerm({
                        idVendedor: newValue?.IdUsuario ?? 0,
                        somenteVendedor: true
                      })
                    }}
                    renderInput={(params) => <TextField {...params} label="Filtrar por vendedor (opcional)" />}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={5} sx={{ ml: canCreate ? 0 : 'auto' }}>
                <TextField
                  onChange={(e) =>
                    debounceSearchTerm({ searchTerm: e.target.value, somenteVendedor: false })
                  }
                  fullWidth
                  label="Buscar cliente"
                  placeholder="Login, razão social, nome fantasia ou CNPJ"
                />
              </Grid>
            )}
          </>
        }
      </Grid>
      <MaterialReactTable table={table} />
    </>
  )
}